import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { InputBaseModel } from '../../models';
import { InputRenderer } from '../../components';
import { ComponentRow, GetComponentRowsFunction, NodeProps } from '../../componentTypes';
import { LayoutRenderer } from '../../layout_renderers';
import { ClassificationInputLayoutUtils, InputIds, NodeIds } from '../../misc';
import { useNodeForm } from '../../hooks';
import { ColumnSpan } from '../../types';
import { InputComponentRegistry } from '../../node_inputs';
import { AdditionalClassificationInput } from '../../node_inputs/SharedInputs';

const InputComponents = InputComponentRegistry[NodeIds.Classifications];

const InputColumnSpans: Record<string, ColumnSpan> = {
    [InputIds.Classifications.MainClass]: 24,
    [InputIds.Classifications.Class]: 24,
    [InputIds.Classifications.SubClass]: 24
};

const StandardClassificationInputIds = [
    InputIds.Classifications.MainClass,
    InputIds.Classifications.Class,
    InputIds.Classifications.SubClass
];

const getInputComponent = (input: InputBaseModel) => {
    const component = InputComponents[input.id as keyof typeof InputComponents];
    if (component) {
        return component;
    }

    return ClassificationInputLayoutUtils.isAdditionalClassificationInput(input)
        ? AdditionalClassificationInput
        : undefined;
};

const getInputComponentRowEntry = (input: InputBaseModel, columnSpan?: ColumnSpan) => ({
    component: <InputRenderer key={input.id} input={input} component={getInputComponent(input)} />,
    columnSpan: columnSpan ?? InputColumnSpans[input.id] ?? 24
});

const getAdditionalClassificationRows = (inputs: InputBaseModel[]): ComponentRow[] =>
    ClassificationInputLayoutUtils.groupAdditionalClassificationInputs(inputs).map(inputGroup =>
        inputGroup.map(input => getInputComponentRowEntry(input, 8))
    );

export const getClassificationComponentRows = (inputs: InputBaseModel[]): ComponentRow[] => {
    const additionalClassificationInputs = ClassificationInputLayoutUtils.getAdditionalClassificationInputs(inputs);
    const renderedStandardInputIds = StandardClassificationInputIds.filter(inputId =>
        inputs.some(input => input.id === inputId)
    );
    const standardParentInputIdByAdditionalInputGuid =
        ClassificationInputLayoutUtils.resolveStandardParentInputIdByAdditionalInputGuid(
            additionalClassificationInputs,
            renderedStandardInputIds
        );
    const renderedInputGuids = new Set<string>();
    const rows: ComponentRow[] = [];

    for (const inputId of StandardClassificationInputIds) {
        const standardInput = inputs.find(input => input.id === inputId);

        if (!standardInput) {
            continue;
        }

        renderedInputGuids.add(standardInput.guid);
        rows.push([getInputComponentRowEntry(standardInput)]);

        const childAdditionalClassificationInputs =
            ClassificationInputLayoutUtils.getAdditionalClassificationInputsForStandardParent(
                additionalClassificationInputs,
                standardParentInputIdByAdditionalInputGuid,
                inputId
            );

        childAdditionalClassificationInputs.forEach(input => renderedInputGuids.add(input.guid));
        rows.push(...getAdditionalClassificationRows(childAdditionalClassificationInputs));
    }

    const standaloneAdditionalClassificationInputs =
        ClassificationInputLayoutUtils.getStandaloneAdditionalClassificationInputs(
            additionalClassificationInputs,
            standardParentInputIdByAdditionalInputGuid
        );

    standaloneAdditionalClassificationInputs.forEach(input => renderedInputGuids.add(input.guid));
    rows.push(...getAdditionalClassificationRows(standaloneAdditionalClassificationInputs));

    inputs
        .filter(input => !renderedInputGuids.has(input.guid))
        .forEach(input => rows.push([getInputComponentRowEntry(input)]));

    return rows;
};

const ClassificationsNodeRenderer: React.FC<NodeProps> = ({ node }: NodeProps) => {
    const { form, formKey, onValuesChange } = useNodeForm(node);

    const getComponentRows: GetComponentRowsFunction = React.useCallback(
        (inputs: InputBaseModel[]) => getClassificationComponentRows(inputs),
        []
    );

    return (
        <LayoutRenderer
            node={node}
            form={form}
            formKey={formKey}
            onValuesChange={onValuesChange}
            getComponentRows={getComponentRows}
        />
    );
};

export default observer(ClassificationsNodeRenderer);
